import AbstractApiService from 'src/services/abstractApiService';
import { api } from 'pages/APIs';

interface OrganizationLogsParams {
  log_level?: string; // Comma-separated: "ERROR,WARN,INFO,DEBUG"
  services?: string; // Comma-separated: "ocr,classification,field_extraction"
  limit?: number; // 1-5000, default 1000
  hours?: number; // 1-168 (max 1 week), default 24
  page?: number; // Page number (1-indexed)
  page_size?: number; // Results per page (1-1000)
  search?: string; // Search query (case-insensitive substring)
}

interface OrganizationMetricsParams {
  page?: number; // Page number (1-indexed, each page = time window)
  hours_per_page?: number; // Hours per page (1-24, default: 1)
  services?: string; // Comma-separated service names
  log_level?: string; // Comma-separated: "ERROR,WARN,INFO,DEBUG"
}

interface LogEntry {
  timestamp: string | number; // ISO 8601 string or epoch milliseconds (after backend fix)
  level: 'ERROR' | 'WARN' | 'INFO' | 'DEBUG';
  module: string;
  message: string;
  metadata?: Record<string, any>;
  organization_name?: string;
}

interface OrganizationLogsResponse {
  organization_id: string;
  organization_name: string;
  total_entries: number;
  accessible_organizations: string[];
  logs: LogEntry[];
  query_info: {
    start_time: string;
    end_time: string;
    time_range_hours: number;
    filters_applied: string[];
    organizations_queried: string[];
    logql_queries: string[];
    total_query_time_ms: number;
  };
  security_context: {
    access_validated: boolean;
    admin_level: string;
    organizations_count: number;
    security_violations_blocked: number;
    incident_id: string;
  };
}

interface OrganizationMetricsResponse {
  severity_counts: {
    error_count: number;
    warning_count: number;
    info_count: number;
    debug_count: number;
    total: number;
  };
  peak_activity?: {
    time: string;          // "14:00"
    timestamp?: string;    // "2026-01-11T14:00:00Z"
    count: number;
  };
  top_source?: {
    name: string;
    count: number;
  };
  time_window?: {
    start: string;
    end: string;
    hours_per_page: number;
  };
  filters_applied?: string[];
}

// Auth Success Rate
interface AuthSuccessRateParams {
  page?: number;
  hours_per_page?: number;
}

interface AuthSuccessRateResponse {
  auth_success_rate: {
    success_rate_percent: number;
    total_attempts: number;
    successful_attempts: number;
    failed_attempts: number;
  };
  time_window: { start: string; end: string; hours_per_page: number };
  query_info: { organization_id: string; namespace: string };
  response_time_ms: number;
}

// Auth Latency
interface AuthLatencyParams {
  page?: number;
  hours_per_page?: number;
}

interface AuthLatencyResponse {
  auth_latency: {
    avg_latency_seconds: number;
    p95_latency_seconds: number;
    p99_latency_seconds: number;
    min_seconds: number;
    max_seconds: number;
    samples: number;
  };
  time_window: { start: string; end: string; hours_per_page: number };
  query_info: { organization_id: string; namespace: string };
  response_time_ms: number;
}

// API Usage
interface ApiUsageParams {
  page?: number;
  hours_per_page?: number;
  top_n?: number;
}

interface ApiUsageResponse {
  api_usage: Array<{ endpoint: string; calls: number }>;
  total_calls: number;
  time_window: { start: string; end: string; hours_per_page: number };
  query_info: { organization_id: string; namespace: string };
  response_time_ms: number;
}

// Timeline Analytics
interface TimelineAnalyticsParams {
  hours?: number;
  bucket?: 'hour' | 'day';
  group_by?: 'level';
  format?: 'timeline';
  levels?: string;
  services?: string;
  status?: string;
  user?: string;
  action?: string;
  q?: string;
}

interface TimelineAnalyticsResponse {
  timeline: Array<{
    hour: string;
    error: number;
    warning: number;
    info: number;
    debug: number;
  }>;
  totals: { count: number };
  query_info: { organization_id: string; namespace: string };
  response_time_ms: number;
}

// Faro Analytics (Frontend RUM logs from Loki)
interface FaroAnalyticsParams {
  hours?: number;
  bucket?: 'hour' | 'day';
  format?: 'timeline' | 'series';
}

interface FaroAnalyticsResponse {
  org_id: string;
  namespace: string;
  data_source: 'loki';
  log_type: 'faro';
  window: {
    start: string;
    end: string;
    bucket: string;
    hours: number;
  };
  timeline: Array<{
    hour: string;
    error: number;
    warning: number;
    info: number;
    debug: number;
  }>;
  totals: { count: number };
}

// Combined Analytics (OpenSearch backend + Loki Faro frontend)
interface CombinedAnalyticsParams {
  hours?: number;
  bucket?: 'hour' | 'day';
  group_by?: 'level' | 'service' | 'status' | 'user' | 'action';
  top_n?: number;
  levels?: string;
  services?: string;
  status?: string;
  user?: string;
  action?: string;
  q?: string;
  format?: 'timeline' | 'series';
  include_faro?: boolean;
}

interface CombinedTimelineEntry {
  hour: string;
  error: number;
  warning: number;
  info: number;
  debug: number;
  // Source breakdown for detailed analysis
  backend_error: number;
  backend_warning: number;
  backend_info: number;
  backend_debug: number;
  frontend_error: number;
  frontend_warning: number;
  frontend_info: number;
  frontend_debug: number;
}

interface CombinedAnalyticsResponse {
  org_id: string;
  namespace: string;
  window: {
    start: string;
    end: string;
    bucket: string;
    hours: number;
  };
  group_by: string;
  top_n: number;
  timeline: CombinedTimelineEntry[];
  totals: {
    count: number;
    backend_count: number;
    frontend_count: number;
  };
  sources: {
    backend: {
      data_source: 'opensearch';
      count: number;
      timeline_entries: number;
    };
    frontend: {
      data_source: 'loki';
      log_type: 'faro';
      count: number;
      timeline_entries: number;
      included: boolean;
    };
  };
  facets: {
    level: Array<{ key: string; count: number }>;
    service: Array<{ key: string; count: number }>;
    status: Array<{ key: string; count: number }>;
    user: Array<{ key: string; count: number }>;
    action: Array<{ key: string; count: number }>;
  };
  filters: {
    services: string[];
    levels: string[];
    status: string[];
    user: string | null;
    action: string | null;
    q: string | null;
  };
}

// Service Timeline Analytics (Logs Over Time by Service with Stacked Levels)
export interface ServiceTimelineParams {
  hours?: number;
  bucket?: 'hour' | 'day';
  top_n?: number;
  services?: string;
}

export interface ServiceLevelCounts {
  error: number;
  warning: number;
  info: number;
  debug: number;
}

export interface ServiceTimelineEntry {
  hour: string;
  services: Record<string, ServiceLevelCounts>;
}

export interface ServiceTotals {
  total: number;
  error: number;
  warning: number;
  info: number;
  debug: number;
}

export interface ServiceTimelineResponse {
  timeline: ServiceTimelineEntry[];
  services: string[];
  totals: Record<string, ServiceTotals>;
  total_count: number;
  bucket: string;
  hours: number;
  time_range: {
    start: string;
    end: string;
  };
}

// Logs Services Endpoint (Enhanced with Log Level Breakdown - GitHub Issue #8639)
export interface LogsServicesParams {
  time_period?: 'today' | 'week' | '30days' | 'custom'; // Preset time periods
  start_time?: string; // ISO 8601 format (required if time_period=custom)
  end_time?: string; // ISO 8601 format (required if time_period=custom)
  log_level?: string; // Comma-separated: "ERROR,WARN,INFO,DEBUG"
  env?: string; // Environment filter (dev, stage, prod)
}

export interface ServiceWithLevels {
  name: string;
  total: number;
  error_count: number;
  warn_count: number;
  info_count: number;
  debug_count: number;
}

export interface LogServicesResponse {
  apps: ServiceWithLevels[];
  components: ServiceWithLevels[];
  services: ServiceWithLevels[];
  metadata: {
    query_succeeded: boolean;
    fallback_used: boolean;
    time_range: {
      start: string; // ISO 8601 with optional microseconds
      end: string; // ISO 8601 with optional microseconds
      period: string; // "today", "week", "30days", or "custom"
    };
    total_logs: number;
    filters_applied: string[];
  };
}

// Log Export API interfaces
export interface LogExportRequestParams {
  time_range_start: string; // ISO 8601 format
  time_range_end: string; // ISO 8601 format
  log_levels?: string; // Comma-separated: "ERROR,WARN,INFO"
  services?: string; // Comma-separated: "api,ocr,extraction"
  search_query?: string; // Optional search query
}

export interface LogExportStatusResponse {
  id: string;
  status: 'pending' | 'processing' | 'ready' | 'failed' | 'expired';
  created_at: string;
  completed_at?: string;
  expires_at?: string;
  file_size_bytes?: number;
  log_count?: number;
  error_message?: string;
  download_available: boolean;
  time_range_start: string;
  time_range_end: string;
  log_levels?: string;
  services?: string;
  search_query?: string;
}

export interface LogExportListResponse {
  exports: LogExportStatusResponse[];
  total: number;
}

export default class ActivityLogsApiService extends AbstractApiService {
  public constructor() {
    super({});
  }

  /**
   * Get organization-wide activity logs for admin users
   *
   * @param params - Query parameters for filtering logs
   * @returns Promise with organization logs response
   *
   * @example
   * ```typescript
   * const logs = await activityLogsApiService.getOrganizationLogs({
   *   log_level: 'ERROR,WARN',
   *   services: 'ocr,classification',
   *   limit: 1000,
   *   hours: 24
   * });
   * ```
   */
  public async getOrganizationLogs(
    params: OrganizationLogsParams,
  ): Promise<OrganizationLogsResponse> {
    const queryParams = new URLSearchParams();

    if (params.log_level) {
      queryParams.append('log_level', params.log_level);
    }

    if (params.services) {
      queryParams.append('services', params.services);
    }

    if (params.limit !== undefined) {
      queryParams.append('limit', params.limit.toString());
    }

    if (params.hours !== undefined) {
      queryParams.append('hours', params.hours.toString());
    }

    if (params.page !== undefined) {
      queryParams.append('page', params.page.toString());
    }

    if (params.page_size !== undefined) {
      queryParams.append('page_size', params.page_size.toString());
    }

    if (params.search) {
      queryParams.append('search', params.search);
    }

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${api.get_organization_logs}?${queryString}`
      : api.get_organization_logs;

    // Use getWithoutOrgId() because this endpoint extracts org_id from JWT token
    // The API does not accept org_id as a query parameter (see issue #13668)
    return this.getWithoutOrgId(endpoint);
  }

  /**
   * Get severity count metrics for organization logs
   *
   * Lightweight endpoint that returns ONLY severity counts (no logs).
   * Uses same time-window pagination and filters as main logs endpoint.
   *
   * @param params - Query parameters for filtering metrics
   * @returns Promise with organization metrics response
   *
   * @example
   * ```typescript
   * const metrics = await activityLogsApiService.getOrganizationMetrics({
   *   page: 1,
   *   hours_per_page: 1,
   *   services: 'ocr,classification'
   * });
   * ```
   */
  public async getOrganizationMetrics(
    params: OrganizationMetricsParams,
  ): Promise<OrganizationMetricsResponse> {
    const queryParams = new URLSearchParams();

    if (params.page !== undefined) {
      queryParams.append('page', params.page.toString());
    }

    if (params.hours_per_page !== undefined) {
      queryParams.append('hours_per_page', params.hours_per_page.toString());
    }

    if (params.services) {
      queryParams.append('services', params.services);
    }

    if (params.log_level) {
      queryParams.append('log_level', params.log_level);
    }

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${api.get_organization_logs_metrics}?${queryString}`
      : api.get_organization_logs_metrics;

    // Use getWithoutOrgId() because this endpoint extracts org_id from JWT token
    return this.getWithoutOrgId(endpoint);
  }

  /**
   * Check health of organization logs functionality
   *
   * @returns Promise with health check response
   */
  public async getOrganizationLogsHealth(): Promise<any> {
    // Use getWithoutOrgId() for consistency - health endpoint also uses JWT token
    return this.getWithoutOrgId(api.get_organization_logs_health);
  }

  /**
   * Fetch available services for the filter dropdown.
   *
   * Calls /api/logs/organization/services which queries OpenSearch for
   * the actual distinct services the org produced in the last 30 days.
   * Without this the dropdown listed hard-coded names like 'import_ftp'
   * that don't match real log entries (which use 'ftp-import'), so
   * picking a service produced 0 hits both server-side and client-side.
   *
   * Falls back to the static list only when the backend call fails — a
   * fresh org with no logs yet should still see SOMETHING in the dropdown.
   *
   * @returns Promise resolving to array of {name, description}.
   */
  public async fetchAvailableServices(): Promise<
    Array<{ name: string; description: string }>
  > {
    try {
      const response = await this.getWithoutOrgId<any>(
        api.get_available_services
      );
      const services = response?.data?.services;
      if (Array.isArray(services) && services.length > 0) {
        return services.map((s: any) => ({
          name: String(s.name),
          description: String(s.name),
        }));
      }
    } catch (err) {
      // Network/auth failure — fall through to the static list below.
      // We intentionally do not surface this to the UI: the dropdown
      // staying populated with a reasonable default is more helpful
      // than an error banner blocking the whole filter row.
      console.warn(
        '[ActivityLogs] /organization/services failed, using static fallback',
        err
      );
    }
    return this.getDefaultServices();
  }

  /**
   * Get default list of available services (fallback)
   * Used when API endpoint is unavailable
   *
   * @returns Array of default service objects
   */
  public getDefaultServices(): Array<{ name: string; description: string }> {
    return [
      // Core Services
      { name: 'api', description: 'API Service' },
      { name: 'web', description: 'Web Application' },
      { name: 'auth', description: 'Authentication Service' },

      // Document Processing
      { name: 'ocr', description: 'OCR Service' },
      { name: 'barcode', description: 'Barcode/QR Code Service' },
      { name: 'classification', description: 'Classification Service' },
      { name: 'field_extraction', description: 'Field Extraction' },
      { name: 'table_extraction', description: 'Table Extraction' },
      { name: 'extraction', description: 'Extraction Service' },

      // Business Logic
      { name: 'po_match', description: 'PO Matching Service' },
      { name: 'transformation', description: 'Transformation Service' },
      { name: 'auto_accounting', description: 'Auto Accounting' },
      { name: 'workflow', description: 'Workflow Service' },

      // Integration
      { name: 'export', description: 'Export Service' },
      { name: 'import_email', description: 'Email Import Service' },
      { name: 'import_ftp', description: 'FTP Import Service' },
      { name: 'notification', description: 'Notification Service' },
      { name: 'storage', description: 'Storage Service' },
      { name: 'webhook', description: 'Webhook Service' },
      { name: 'integration', description: 'Integration Service' },
    ];
  }

  /**
   * Get authentication success rate metrics
   *
   * @param params - Pagination parameters (page, hours_per_page)
   * @returns Promise with auth success rate response
   */
  public async getAuthSuccessRate(
    params: AuthSuccessRateParams = {},
  ): Promise<AuthSuccessRateResponse> {
    const queryParams = new URLSearchParams();

    if (params.page !== undefined) {
      queryParams.append('page', params.page.toString());
    }

    if (params.hours_per_page !== undefined) {
      queryParams.append('hours_per_page', params.hours_per_page.toString());
    }

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${api.get_organization_logs_auth_success_rate}?${queryString}`
      : api.get_organization_logs_auth_success_rate;

    return this.getWithoutOrgId(endpoint);
  }

  /**
   * Get authentication latency metrics (avg, P95, P99, min, max)
   *
   * @param params - Pagination parameters (page, hours_per_page)
   * @returns Promise with auth latency response
   */
  public async getAuthLatency(
    params: AuthLatencyParams = {},
  ): Promise<AuthLatencyResponse> {
    const queryParams = new URLSearchParams();

    if (params.page !== undefined) {
      queryParams.append('page', params.page.toString());
    }

    if (params.hours_per_page !== undefined) {
      queryParams.append('hours_per_page', params.hours_per_page.toString());
    }

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${api.get_organization_logs_auth_latency}?${queryString}`
      : api.get_organization_logs_auth_latency;

    return this.getWithoutOrgId(endpoint);
  }

  /**
   * Get API usage metrics (top N endpoints by call count)
   *
   * @param params - Pagination and filter parameters (page, hours_per_page, top_n)
   * @returns Promise with API usage response
   */
  public async getApiUsage(
    params: ApiUsageParams = {},
  ): Promise<ApiUsageResponse> {
    const queryParams = new URLSearchParams();

    if (params.page !== undefined) {
      queryParams.append('page', params.page.toString());
    }

    if (params.hours_per_page !== undefined) {
      queryParams.append('hours_per_page', params.hours_per_page.toString());
    }

    if (params.top_n !== undefined) {
      queryParams.append('top_n', params.top_n.toString());
    }

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${api.get_organization_logs_api_usage}?${queryString}`
      : api.get_organization_logs_api_usage;

    return this.getWithoutOrgId(endpoint);
  }

  /**
   * Get timeline analytics with hourly severity breakdown
   *
   * @param params - Time range and formatting parameters
   * @returns Promise with timeline analytics response
   */
  public async getTimelineAnalytics(
    params: TimelineAnalyticsParams = {},
  ): Promise<TimelineAnalyticsResponse> {
    const queryParams = new URLSearchParams();

    if (params.hours !== undefined) {
      queryParams.append('hours', params.hours.toString());
    }

    if (params.bucket) {
      queryParams.append('bucket', params.bucket);
    }

    if (params.group_by) {
      queryParams.append('group_by', params.group_by);
    }

    if (params.format) {
      queryParams.append('format', params.format);
    }

    if (params.levels) {
      queryParams.append('levels', params.levels);
    }

    if (params.services) {
      queryParams.append('services', params.services);
    }

    if (params.status) {
      queryParams.append('status', params.status);
    }

    if (params.user) {
      queryParams.append('user', params.user);
    }

    if (params.action) {
      queryParams.append('action', params.action);
    }

    if (params.q) {
      queryParams.append('q', params.q);
    }

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${api.get_organization_logs_analytics_timeline}?${queryString}`
      : api.get_organization_logs_analytics_timeline;

    return this.getWithoutOrgId(endpoint);
  }

  /**
   * Get logs services with log level breakdown
   *
   * Enhanced endpoint that returns service/component/app aggregations
   * with detailed log level counts (ERROR, WARN, INFO, DEBUG).
   *
   * Based on GitHub Issue #8639 - Backend uses OpenSearch nested aggregations
   * to provide log level breakdown per service/component/app.
   *
   * @param params - Query parameters for time range and filtering
   * @returns Promise with logs services response including level breakdown
   *
   * @example
   * ```typescript
   * // Get last week's data
   * const data = await activityLogsApiService.getLogsServices({
   *   time_period: 'week',
   *   env: 'dev'
   * });
   *
   * // Get custom date range
   * const data = await activityLogsApiService.getLogsServices({
   *   time_period: 'custom',
   *   start_time: '2026-01-01T00:00:00Z',
   *   end_time: '2026-01-10T23:59:59Z',
   *   log_level: 'ERROR,WARN'
   * });
   * ```
   */
  public async getLogsServices(
    params: LogsServicesParams = {},
  ): Promise<LogServicesResponse> {
    const queryParams = new URLSearchParams();

    if (params.time_period) {
      queryParams.append('time_period', params.time_period);
    }

    if (params.start_time) {
      queryParams.append('start_time', params.start_time);
    }

    if (params.end_time) {
      queryParams.append('end_time', params.end_time);
    }

    if (params.log_level) {
      queryParams.append('log_level', params.log_level);
    }

    if (params.env) {
      queryParams.append('env', params.env);
    }

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${api.get_available_services}?${queryString}`
      : api.get_available_services;

    return this.getWithoutOrgId(endpoint);
  }

  /**
   * Get Faro RUM analytics from Loki
   *
   * Returns frontend Real User Monitoring (RUM) log analytics aggregated by
   * timestamp and severity level. Data comes from Grafana Faro SDK via Loki.
   *
   * @param params - Query parameters for time range and formatting
   * @returns Promise with Faro analytics response including timeline
   *
   * @example
   * ```typescript
   * const faroData = await activityLogsApiService.getFaroAnalytics({
   *   hours: 24,
   *   bucket: 'hour',
   * });
   * ```
   */
  public async getFaroAnalytics(
    params: FaroAnalyticsParams = {},
  ): Promise<FaroAnalyticsResponse> {
    const queryParams = new URLSearchParams();

    if (params.hours !== undefined) {
      queryParams.append('hours', params.hours.toString());
    }

    if (params.bucket) {
      queryParams.append('bucket', params.bucket);
    }

    if (params.format) {
      queryParams.append('format', params.format);
    }

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${api.get_organization_logs_analytics_faro}?${queryString}`
      : api.get_organization_logs_analytics_faro;

    return this.getWithoutOrgId(endpoint);
  }

  /**
   * Get combined analytics from OpenSearch (backend) and Loki (Faro frontend)
   *
   * Returns merged log analytics from both backend services (OpenSearch) and
   * frontend RUM (Faro/Loki), providing a unified view of all system logs.
   * Timeline includes source breakdown (backend vs frontend) for detailed analysis.
   *
   * @param params - Query parameters for time range, filtering, and formatting
   * @returns Promise with combined analytics response including merged timeline
   *
   * @example
   * ```typescript
   * // Get combined analytics with default settings
   * const combined = await activityLogsApiService.getCombinedAnalytics({
   *   hours: 24,
   *   bucket: 'hour',
   *   group_by: 'level',
   *   format: 'timeline',
   * });
   *
   * // Get backend-only analytics (no Faro)
   * const backendOnly = await activityLogsApiService.getCombinedAnalytics({
   *   hours: 24,
   *   include_faro: false,
   * });
   * ```
   */
  public async getCombinedAnalytics(
    params: CombinedAnalyticsParams = {},
  ): Promise<CombinedAnalyticsResponse> {
    const queryParams = new URLSearchParams();

    if (params.hours !== undefined) {
      queryParams.append('hours', params.hours.toString());
    }

    if (params.bucket) {
      queryParams.append('bucket', params.bucket);
    }

    if (params.group_by) {
      queryParams.append('group_by', params.group_by);
    }

    if (params.top_n !== undefined) {
      queryParams.append('top_n', params.top_n.toString());
    }

    if (params.levels) {
      queryParams.append('levels', params.levels);
    }

    if (params.services) {
      queryParams.append('services', params.services);
    }

    if (params.status) {
      queryParams.append('status', params.status);
    }

    if (params.user) {
      queryParams.append('user', params.user);
    }

    if (params.action) {
      queryParams.append('action', params.action);
    }

    if (params.q) {
      queryParams.append('q', params.q);
    }

    if (params.format) {
      queryParams.append('format', params.format);
    }

    if (params.include_faro !== undefined) {
      queryParams.append('include_faro', params.include_faro.toString());
    }

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${api.get_organization_logs_analytics_combined}?${queryString}`
      : api.get_organization_logs_analytics_combined;

    return this.getWithoutOrgId(endpoint);
  }

  /**
   * Get service timeline analytics - Logs over time grouped by service with stacked levels.
   *
   * Returns timeline data for "Logs Over Time" charts with:
   * - X-axis: time (hours or days)
   * - Y-axis: log count
   * - Grouped by: service (api, barcode, celery, etc.)
   * - Stacked by: level (error, warning, info, debug)
   *
   * @param params - Query parameters for time range and filtering
   * @returns Promise with service timeline response
   *
   * @example
   * ```typescript
   * // Get service timeline for today
   * const timeline = await activityLogsApiService.getServiceTimelineAnalytics({
   *   hours: 24,
   *   bucket: 'hour',
   *   top_n: 10,
   * });
   *
   * // Get service timeline for 30 days
   * const timeline30d = await activityLogsApiService.getServiceTimelineAnalytics({
   *   hours: 720,
   *   bucket: 'day',
   * });
   * ```
   */
  public async getServiceTimelineAnalytics(
    params: ServiceTimelineParams = {},
  ): Promise<ServiceTimelineResponse> {
    const queryParams = new URLSearchParams();

    if (params.hours !== undefined) {
      queryParams.append('hours', params.hours.toString());
    }

    if (params.bucket) {
      queryParams.append('bucket', params.bucket);
    }

    if (params.top_n !== undefined) {
      queryParams.append('top_n', params.top_n.toString());
    }

    if (params.services) {
      queryParams.append('services', params.services);
    }

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `${api.get_organization_logs_analytics_service_timeline}?${queryString}`
      : api.get_organization_logs_analytics_service_timeline;

    return this.getWithoutOrgId(endpoint);
  }

  // ============================================================================
  // Log Export API Methods (Async export with S3 storage)
  // ============================================================================

  /**
   * Request a new log export
   *
   * Creates an async export request that will be processed by a Celery task.
   * The export will be available for download once status becomes 'ready'.
   *
   * @param params - Export request parameters including time range and filters
   * @returns Promise with export status response including export ID
   *
   * @example
   * ```typescript
   * const export = await activityLogsApiService.requestLogExport({
   *   time_range_start: '2026-01-01T00:00:00Z',
   *   time_range_end: '2026-01-22T23:59:59Z',
   *   log_levels: 'ERROR,WARN',
   *   services: 'api,ocr',
   * });
   * console.log('Export ID:', export.id);
   * ```
   */
  public async requestLogExport(
    params: LogExportRequestParams,
  ): Promise<LogExportStatusResponse> {
    return this.postWithoutOrgId(api.logs_export_request, params);
  }

  /**
   * Get list of recent log exports for the organization
   *
   * Returns exports from the last 24 hours with their current status.
   * Use this for polling while exports are pending/processing.
   *
   * @param limit - Maximum number of exports to return (default: 10)
   * @returns Promise with list of export statuses
   *
   * @example
   * ```typescript
   * const exports = await activityLogsApiService.getLogExportList();
   * const pendingExports = exports.exports.filter(e => e.status === 'pending');
   * ```
   */
  public async getLogExportList(limit = 10): Promise<LogExportListResponse> {
    const endpoint = `${api.logs_export_status}?limit=${limit}`;
    return this.getWithoutOrgId(endpoint);
  }

  /**
   * Get status of a specific log export
   *
   * @param exportId - UUID of the export request
   * @returns Promise with export status response
   *
   * @example
   * ```typescript
   * const status = await activityLogsApiService.getLogExportStatus('abc-123');
   * if (status.download_available) {
   *   // Ready to download
   * }
   * ```
   */
  public async getLogExportStatus(
    exportId: string,
  ): Promise<LogExportStatusResponse> {
    const endpoint = `${api.logs_export_status}/${exportId}`;
    return this.getWithoutOrgId(endpoint);
  }

  /**
   * Get download URL for a completed log export
   *
   * Returns the full URL to download the export ZIP file.
   * The file is streamed through the API (S3 is not public).
   *
   * @param exportId - UUID of the export request
   * @returns Full download URL
   */
  public getLogExportDownloadUrl(exportId: string): string {
    return `${api.logs_export_download}/${exportId}`;
  }

  /**
   * Delete/cancel a log export request
   *
   * @param exportId - UUID of the export request to delete
   * @returns Promise with deletion result
   */
  public async deleteLogExport(
    exportId: string,
  ): Promise<{ status: string; export_id: string }> {
    // Use the base URL pattern from logs_export_status but without /status suffix
    const baseUrl = api.logs_export_status.replace('/status', '');
    const endpoint = `${baseUrl}/${exportId}`;
    return this.deleteWithoutOrgId(endpoint);
  }
}
